import { useMutation, useQueryClient } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers';
import { useAuth } from '@clerk/clerk-react';
import { ENTITLEMENTS_SUPPORT_OVERVIEW_QUERY_KEY } from '@/state/queries/entitlements/use-entitlements-support-overview';
import { QUOTA_BALANCES_QUERY_KEY, invalidateAllQuotaBalances } from '@/state/queries/entitlements/use-quota-balances';

export type AdminGrantEntitlementBody = {
  kind: 'QUOTA' | 'FEATURE';
  scopeType: string;
  scopeId: string;
  quotaKey?: string;
  quantity?: number;
  featureKey?: string;
  validFrom?: string;
  validUntil?: string;
  idempotencyKey?: string;
};

export type AdminEntitlementIdBody = { entitlementId: string };

export type AdminAdjustQuotaBody = {
  scopeType: string;
  scopeId: string;
  quotaKey: string;
  delta: number;
};

function invalidateScopeEntitlementsQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  scopeType: string,
  scopeId: string,
) {
  queryClient.invalidateQueries({ queryKey: [ENTITLEMENTS_SUPPORT_OVERVIEW_QUERY_KEY, scopeType, scopeId] });
  queryClient.invalidateQueries({ queryKey: [QUOTA_BALANCES_QUERY_KEY, scopeType, scopeId] });
  void invalidateAllQuotaBalances(queryClient);
}

export function useAdminGrantEntitlementMutation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: AdminGrantEntitlementBody) => {
      const token = await getToken();
      const url = `${import.meta.env.VITE_API_URL}/entitlements/admin/grant`;
      return sendJSONRequest({ url, method: METHOD.POST, body, token: token ?? '' }) as Promise<{ id: string }>;
    },
    onSuccess: (_data, body) => {
      invalidateScopeEntitlementsQueries(queryClient, body.scopeType, body.scopeId);
    },
  });
}

export function useAdminRevokeEntitlementMutation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: AdminEntitlementIdBody) => {
      const token = await getToken();
      const url = `${import.meta.env.VITE_API_URL}/entitlements/admin/revoke`;
      await sendJSONRequest({ url, method: METHOD.POST, body, token: token ?? '' });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ENTITLEMENTS_SUPPORT_OVERVIEW_QUERY_KEY] });
      void invalidateAllQuotaBalances(queryClient);
    },
  });
}

export function useAdminExpireEntitlementMutation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: AdminEntitlementIdBody) => {
      const token = await getToken();
      const url = `${import.meta.env.VITE_API_URL}/entitlements/admin/expire`;
      await sendJSONRequest({ url, method: METHOD.POST, body, token: token ?? '' });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ENTITLEMENTS_SUPPORT_OVERVIEW_QUERY_KEY] });
      void invalidateAllQuotaBalances(queryClient);
    },
  });
}

export function useAdminAdjustQuotaMutation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: AdminAdjustQuotaBody) => {
      const token = await getToken();
      const url = `${import.meta.env.VITE_API_URL}/entitlements/admin/adjust`;
      await sendJSONRequest({ url, method: METHOD.POST, body, token: token ?? '' });
    },
    onSuccess: (_data, body) => {
      invalidateScopeEntitlementsQueries(queryClient, body.scopeType, body.scopeId);
    },
  });
}
